import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Sparkles, Crown, AlertCircle, Flame, Infinity, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/api";
import { useAuth } from "@/context/AuthContext";

export default function SuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { refresh } = useAuth();

  const [status, setStatus] = useState("checking");
  const [planType, setPlanType] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { navigate("/"); return; }

    const pollStatus = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          setPlanType(data.plan_type);
          await refresh();
          toast.success("Welcome to Nail Coach Premium! 💅✨");
        } else if (data.status === "expired") {
          setStatus("expired");
        } else if (pollAttempts < 15) {
          setPollAttempts((p) => p + 1);
          setTimeout(pollStatus, 2000);
        } else {
          setStatus("pending");
        }
      } catch (error) {
        if (pollAttempts < 15) {
          setPollAttempts((p) => p + 1);
          setTimeout(pollStatus, 2000);
        } else {
          setStatus("error");
        }
      }
    };
    pollStatus();
  }, [sessionId, navigate, pollAttempts, refresh]);

  const getPlanName = () => {
    switch (planType) {
      case "monthly": return "Monthly Plan";
      case "yearly": return "Yearly Plan";
      case "lifetime": return "Lifetime Access";
      default: return "Premium Access";
    }
  };

  if (status === "checking") {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-heavy rounded-3xl p-8 text-center max-w-md">
          <Sparkles className="w-16 h-16 text-primary mx-auto animate-spin mb-4" />
          <h2 className="font-playfair text-2xl font-bold text-foreground mb-2">Confirming your payment...</h2>
          <p className="text-muted-foreground">Please wait while we verify your subscription</p>
        </motion.div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4" data-testid="success-page">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-heavy rounded-3xl p-8 text-center max-w-md">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.6 }} className="mb-6">
            <div className="relative inline-block">
              <CheckCircle className="w-20 h-20 text-green-500" />
              <Crown className="w-8 h-8 text-amber-500 absolute -top-2 -right-2" />
            </div>
          </motion.div>
          <h1 className="font-playfair text-3xl font-bold text-foreground mb-2">You're In! 💅✨</h1>
          <p className="text-lg text-primary font-semibold mb-2">{getPlanName()} Activated</p>
          <p className="text-muted-foreground mb-8">All premium coaching features are now unlocked. Time to track your nail glow-up! 🌶️</p>
          <Button onClick={() => navigate("/dashboard")} className="w-full rounded-full py-6 text-lg font-semibold bg-primary hover:bg-primary/90 shadow-soft hover:shadow-glow transition-all" data-testid="go-dashboard-btn">
            <TrendingUp className="w-5 h-5 mr-2" /> Go to my Dashboard
          </Button>
          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-sm font-medium text-foreground mb-4">Your unlocked benefits:</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Infinity className="w-4 h-4 text-primary" /><span>Unlimited analyses</span></div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><TrendingUp className="w-4 h-4 text-green-500" /><span>Progress tracking</span></div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Flame className="w-4 h-4 text-rose-500" /><span>Challenges & streaks</span></div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Crown className="w-4 h-4 text-purple-500" /><span>AI Beauty Report</span></div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="glass-heavy rounded-3xl p-8 text-center max-w-md">
        <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
        <h2 className="font-playfair text-2xl font-bold text-foreground mb-2">{status === "expired" ? "Session Expired" : "Something Went Wrong"}</h2>
        <p className="text-muted-foreground mb-6">{status === "expired" ? "Your payment session has expired. Please try again." : "We couldn't verify your payment. If you were charged, please contact support."}</p>
        <Button onClick={() => navigate("/")} className="rounded-full px-8 py-6 bg-primary hover:bg-primary/90">Back to Home</Button>
      </motion.div>
    </div>
  );
}
